/**
 * KML export — turn a Field Plan job's pins + line runs into a KML string
 * that Google Earth, Google My Maps and most GIS tools understand. Downloads
 * as `field-plan_<jobname>.kml`.
 */
import { getUtil } from "@/constants/utilities";

const escapeXml = (s) => String(s ?? "").replace(/[<>&'"]/g, (c) => (
  { "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", "\"": "&quot;" }[c]
));

// AABBGGRR (KML colour byte order is A-B-G-R!)
const hexToKmlColor = (hex) => {
  const h = hex.replace("#", "");
  const r = h.slice(0, 2), g = h.slice(2, 4), b = h.slice(4, 6);
  return ("ff" + b + g + r).toLowerCase();
};

export function buildJobKml(job) {
  const svcSet = new Set([
    ...(job.pins || []).map((p) => p.service),
    ...(job.lines || []).map((l) => l.service),
  ]);
  const styles = Array.from(svcSet).map((svc) => {
    const u = getUtil(svc);
    const kmlColor = hexToKmlColor(u.color);
    return `
    <Style id="pin-${svc}">
      <IconStyle>
        <color>${kmlColor}</color>
        <scale>1.1</scale>
        <Icon><href>http://maps.google.com/mapfiles/kml/paddle/wht-blank.png</href></Icon>
      </IconStyle>
      <LabelStyle><scale>0.9</scale></LabelStyle>
    </Style>
    <Style id="line-${svc}">
      <LineStyle>
        <color>${kmlColor}</color>
        <width>4</width>
      </LineStyle>
      <PolyStyle><fill>0</fill></PolyStyle>
    </Style>`;
  }).join("");

  const pins = (job.pins || []).map((p) => {
    const util = getUtil(p.service);
    const depth = p.depth_mm != null ? ` @ ${p.depth_mm} mm` : "";
    const desc = [p.label, depth && depth.trim(), p.notes].filter(Boolean).join(" · ");
    return `
    <Placemark>
      <name>${escapeXml(util.label + (p.label ? " — " + p.label : ""))}</name>
      <description>${escapeXml(desc)}</description>
      <styleUrl>#pin-${p.service}</styleUrl>
      <Point><coordinates>${p.lng},${p.lat},0</coordinates></Point>
    </Placemark>`;
  }).join("");

  const lines = (job.lines || []).map((ln) => {
    const util = getUtil(ln.service);
    const coords = ln.coords.map(([lat, lng]) => `${lng},${lat},0`).join(" ");
    return `
    <Placemark>
      <name>${escapeXml(util.label + (ln.label ? " — " + ln.label : ""))}</name>
      <styleUrl>#line-${ln.service}</styleUrl>
      <LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>
    </Placemark>`;
  }).join("");

  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(job.name || "Field Plan job")}</name>
    <description>${escapeXml(job.address || "")}</description>
    ${styles}
    ${pins}
    ${lines}
  </Document>
</kml>`;

  const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `field-plan_${(job.name || "site").replace(/[^\w-]+/g, "_")}.kml`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
